import { useCallback, useContext, useEffect, useMemo, useState } from 'react'

import { copyToClipboard } from '@lib/helpers'
import { PrintShopFormContext } from '@lib/print/print-shop-form-context'
import { PrintShopFormStringsContext } from '@lib/print/print-shop-form-strings-context'
import {
  getReviewSectionDescription,
  getReviewSectionTitle,
} from '@lib/print/text'
import { ShopFormStepProps } from '@lib/print/types'
import { getPageUrl, PageType } from '@lib/routes'
import { SiteContext } from '@lib/site'

import ReviewStepReviewSection from './review-section'
import ReviewStepShopLink from './shop-link'

const ReviewStep = ({}: ShopFormStepProps) => {
  const { settings } = useContext(SiteContext)
  const { printSlots, shopState } = useContext(PrintShopFormContext)
  const { shopFormStrings } = useContext(PrintShopFormStringsContext)

  const [isLinkCopied, setIsLinkCopied] = useState(false)

  const shopUrl = useMemo(() => {
    if (!shopState.slug) {
      return ''
    }

    const pageUrl = getPageUrl(PageType.PRIVATE_SHOP_PAGE, [shopState.slug])

    return `${settings?.siteURL}${pageUrl}`
  }, [settings?.siteURL, shopState.id])

  const printSlotTypes = useMemo(() => {
    return printSlots.map((printSlot) => printSlot.type)
  }, [printSlots])

  const handleCopyUrlClick = useCallback(() => {
    copyToClipboard(shopUrl)

    setIsLinkCopied(true)
  }, [shopUrl])

  // Unset URL copied status after 3 seconds
  useEffect(() => {
    let timeout: NodeJS.Timeout | undefined

    if (isLinkCopied) {
      timeout = setTimeout(() => {
        setIsLinkCopied(false)
      }, 3000)
    }

    return () => {
      if (timeout) {
        clearTimeout(timeout)
      }
    }
  }, [isLinkCopied])

  return (
    <div className="max-w-[900px] mx-auto">
      <div className="space-y-2.5 sm:space-y-5">
        <ReviewStepShopLink />

        {printSlotTypes.map((printSlotType) => {
          const key = `print-review-section-${printSlotType}`
          const title = getReviewSectionTitle(shopFormStrings, printSlotType)
          const description = getReviewSectionDescription(
            shopFormStrings,
            printSlotType,
          )

          return (
            <ReviewStepReviewSection
              key={key}
              title={title}
              description={description}
              printSlotType={printSlotType}
            />
          )
        })}
      </div>
    </div>
  )
}

export default ReviewStep
